// CheckoutScreen — receipt-review redesign per
// /Users/trevorfallbacher/.claude/plans/luminous-juggling-pike.md
//
// One unified scan list. Each row collapses to a status-at-a-glance
// summary (paired: tier + receipt rawText + price; unpaired: rose tint
// + "why" diagnostic), and expands on tap into the picker scaffold
// — full 3-axis comparison (UPC, canonical, brand) against every
// receipt line, with the currently-paired line highlighted and other-
// claimed lines dimmed. Tap a candidate to re-pair; "mark as no
// match" inside the picker for the rare unpair-without-replacement.
//
// Above the list: hero card (total, scan/paired counts, store name,
// thumbnail, tier-segmented progress bar), OCR diagnostic strip,
// receipt CTA. Below: notReady warning band (when applicable) and
// the sticky commit footer (outlined leaf when no receipt, filled
// leaf primary after one is loaded).
//
// Per the beta-audit-transparency rule: every scan and every receipt
// line shows ALL identity axes (UPC, canonical_id, brand) inline.
// Every auto-pair has a manual override path. OCR diagnostics surface
// verbatim. No information is hidden behind admin flags.

import { useEffect, useMemo, useRef, useState } from "react";
import {
  useShopTrip,
  tripActions,
  selectScansArray,
} from "../../lib/useShopTrip";
import { rollupSpend, formatCents } from "../../lib/spendRollup";
import { commitTrip, matchReceiptLines } from "./commitTrip";
import { compressImage } from "../../lib/compressImage";
import { supabase } from "../../lib/supabase";
import { COLOR, FONT, RADIUS, SPACE } from "../../lib/tokens";
import { useIngredientInfo } from "../../lib/useIngredientInfo";
import { readinessFor } from "../../lib/canonicalReadiness";
import { findIngredient } from "../../data/ingredients";
import { buildDisplayName } from "../../lib/pantryHelpers";
import { readCache, writeCache, invalidateCache } from "../../lib/useCachedQuery";

// Persist the in-progress checkout (receipt photo, OCR'd lines,
// matcher result, manual overrides) to localStorage keyed by trip
// ID. Refresh / tab-close / nav-away no longer wipes the work. TTL
// is generous because trips can sit open across days and the data
// is small per trip (~80–200 KB with a compressed photo).
const CHECKOUT_CACHE_KEY = (tripId) => `checkout.v1.${tripId}`;
const CHECKOUT_CACHE_TTL_MS = 30 * 24 * 60 * 60 * 1000; // 30 days

// Build the canonical-registry subset that scan-receipt receives.
// Without this, Sonnet sees an empty registry and EVERY receipt line
// returns canonicalId=null, which kills tier 2 + tier 3 in the matcher
// (both require a non-null canonical on the receipt side).
function buildTripCanonicalRegistry(scans) {
  const ids = new Set();
  for (const s of scans) {
    if (s.canonicalId) ids.add(s.canonicalId);
  }
  const rows = [];
  for (const id of ids) {
    const ing = findIngredient(id);
    if (!ing) continue;
    rows.push({
      id: ing.id,
      name: ing.name || ing.id,
      category: ing.category || "pantry",
      units: Array.isArray(ing.units) && ing.units.length ? ing.units : ["count"],
    });
  }
  return rows;
}

// Tier metadata. Color drives both the hero progress bar and the
// inline tier-prefix on each paired row.
const TIERS = [
  { key: "upc",             label: "UPC",    color: COLOR.leaf },
  { key: "canonical+brand", label: "BRAND",  color: COLOR.sky },
  { key: "canonical",       label: "TYPE",   color: COLOR.muted },
  { key: "fuzzy",           label: "TEXT",   color: COLOR.roseDim },
  { key: "manual",          label: "MANUAL", color: COLOR.ink },
];
const TIER_BY_KEY = Object.fromEntries(TIERS.map((t) => [t.key, t]));

// Same UPC normalizer the matcher uses (commitTrip.js).
const normalizeUpc = (s) => {
  if (!s) return null;
  const d = String(s).replace(/\D/g, "").replace(/^0+/, "");
  return d || null;
};

// Render a 3-axis identity strip with an explicit side label.
// `side` is e.g. "scan" or "recpt" — appears as a leading 5-char
// mono prefix so two strips align column-wise for digit-by-digit
// audit when stacked. UPC stays in tabular-nums; canon/brand fall
// back to em-dash if missing.
const IdentityStrip = ({ side, upc, canonicalId, brand, sideColor }) => (
  <div style={{
    display: "flex",
    gap: SPACE.sm,
    fontFamily: FONT.mono,
    fontSize: 12,
    marginTop: 4,
    overflow: "hidden",
    whiteSpace: "nowrap",
    letterSpacing: "0.02em",
    color: COLOR.muted,
  }}>
    <span style={{
      flex: "0 0 auto",
      width: 44,
      color: sideColor || COLOR.muted,
      textTransform: "uppercase",
      letterSpacing: "0.14em",
      fontWeight: 600,
    }}>
      {side}
    </span>
    <span style={{
      flex: 1,
      overflow: "hidden",
      textOverflow: "ellipsis",
    }}>
      <span style={{ fontVariantNumeric: "tabular-nums" }}>
        upc {upc || "—"}
      </span>
      {" · canon "}{canonicalId || "—"}
      {" · brand "}{brand || "—"}
    </span>
  </div>
);

// Diagnose why a scan didn't auto-pair. Walks the same tier checks
// the matcher did, in the same order.
function explainUnmatched(scan, receiptLines) {
  if (!Array.isArray(receiptLines) || receiptLines.length === 0) {
    return "no receipt lines to match against";
  }
  const reasons = [];
  const scanUpc = normalizeUpc(scan.barcodeUpc);
  if (!scanUpc) {
    reasons.push("scan has no UPC");
  } else {
    const upcMatch = receiptLines.some((l) => {
      const ln = normalizeUpc(l.barcode);
      if (ln === scanUpc) return true;
      const raw = String(l.rawText || "").replace(/\D/g, "");
      return raw.includes(scanUpc) || raw.includes(scanUpc.slice(0, -1));
    });
    if (!upcMatch) reasons.push(`UPC ${scan.barcodeUpc} not on receipt`);
  }
  if (!scan.canonicalId) {
    reasons.push("no canonical_id bound");
  } else {
    const canonMatch = receiptLines.some((l) => l.canonicalId === scan.canonicalId);
    if (!canonMatch) reasons.push(`canonical "${scan.canonicalId}" missing from all lines`);
  }
  if (reasons.length === 0) reasons.push("combined-signal score below threshold");
  return reasons.join(" · ");
}

const sectionLabel = {
  fontFamily: FONT.mono,
  fontSize: 12,
  letterSpacing: "0.18em",
  textTransform: "uppercase",
  color: COLOR.muted,
};

export default function CheckoutScreen({ userId, isAdmin = false, onComplete, onBack }) {
  const scans = useShopTrip(selectScansArray);
  const trip = useShopTrip((s) => s.activeTrip);
  const placementOverrides = useShopTrip((s) => s.placementOverrides);
  // Title for a scan row. Canonical-bound scans compose the full
  // [Brand] [State] [Canonical] [Cut] ([size]) identity via
  // buildDisplayName — never hand-rolled, never raw marketing copy
  // (identity rule in CLAUDE.md). User-typed sizes live in
  // placementOverrides, not the scan row, so the override feeds the
  // suffix. Unbound scans fall back to scan.displayName /
  // product_name, which classifyScan already populated from the
  // correction tier or flavor-stripped OFF text — raw OFF marketing
  // never lands there. The raw-data diagnostic strip below keeps
  // echoing the stored fields verbatim; this helper is for TITLES.
  const scanTitle = (scan) => {
    if (scan?.canonicalId) {
      const override = placementOverrides?.get?.(scan.id) || {};
      const composed = buildDisplayName({
        canonicalId: scan.canonicalId,
        brand: scan.brand || null,
        state: override.state ?? null,
        cut:   override.cut ?? null,
        max:   override.packageAmount ?? override.max ?? null,
        unit:  override.packageUnit ?? null,
      });
      if (composed && composed !== "Untitled") return composed;
    }
    return scan?.displayName || scan?.productName?.trim() || null;
  };
  const [phase, setPhase] = useState("summary");
  const [imageData, setImageData] = useState(null);
  const [receiptMeta, setReceiptMeta] = useState({ store: null, date: null, totalCents: null });
  const [autoPairs, setAutoPairs] = useState(new Map());
  const [manualPairs, setManualPairs] = useState(new Map());
  const [receiptLines, setReceiptLines] = useState([]);
  const [ocrDiagnostic, setOcrDiagnostic] = useState(null);
  const [error, setError] = useState(null);
  const [result, setResult] = useState(null);
  const [expandedScanId, setExpandedScanId] = useState(null);
  // Delete is a two-tap pattern matching KitchenCard. First tap arms
  // (✕ → ‼ in rose), second tap within DELETE_ARM_MS commits.
  // Auto-disarms after the timeout. No browser-native confirm
  // dialog — `window.confirm` is the cardinal-sin off-brand modal.
  const [armedDeleteScanId, setArmedDeleteScanId] = useState(null);
  const DELETE_ARM_MS = 4000;
  useEffect(() => {
    if (!armedDeleteScanId) return undefined;
    const t = setTimeout(() => setArmedDeleteScanId(null), DELETE_ARM_MS);
    return () => clearTimeout(t);
  }, [armedDeleteScanId]);

  // ── Persistence ── hydrate from localStorage on trip change;
  // serialize on every relevant state change so refresh / tab-close
  // doesn't lose the receipt scan + manual pairings the user made.
  // Auto-pairs recompute from scans + receiptLines on hydrate
  // because they're a pure function of those inputs.
  const hydratedTripIdRef = useRef(null);
  useEffect(() => {
    if (!trip?.id) return;
    if (hydratedTripIdRef.current === trip.id) return;
    hydratedTripIdRef.current = trip.id;
    const cached = readCache(CHECKOUT_CACHE_KEY(trip.id), CHECKOUT_CACHE_TTL_MS);
    if (!cached) return;
    if (cached.imageData) setImageData(cached.imageData);
    if (cached.receiptMeta) setReceiptMeta(cached.receiptMeta);
    if (Array.isArray(cached.receiptLines) && cached.receiptLines.length > 0) {
      setReceiptLines(cached.receiptLines);
      setAutoPairs(matchReceiptLines(scans, cached.receiptLines));
    }
    if (Array.isArray(cached.manualPairs)) {
      setManualPairs(new Map(cached.manualPairs));
    }
    if (cached.ocrDiagnostic) setOcrDiagnostic(cached.ocrDiagnostic);
  }, [trip?.id, scans]);

  useEffect(() => {
    if (!trip?.id) return;
    if (hydratedTripIdRef.current !== trip.id) return; // wait for hydrate
    const empty = !imageData && receiptLines.length === 0 && manualPairs.size === 0;
    if (empty) return;
    writeCache(CHECKOUT_CACHE_KEY(trip.id), {
      imageData,
      receiptMeta,
      receiptLines,
      manualPairs: [...manualPairs.entries()],
      ocrDiagnostic,
    });
  }, [trip?.id, imageData, receiptMeta, receiptLines, manualPairs, ocrDiagnostic]);

  // Effective pair map: manual overrides win. A null in manualPairs
  // means "forced unmatched" (user chose mark-as-no-match).
  const effectivePairs = useMemo(() => {
    const merged = new Map();
    for (const [id, p] of autoPairs) merged.set(id, p);
    for (const [id, p] of manualPairs) {
      if (p === null) merged.delete(id);
      else merged.set(id, p);
    }
    return merged;
  }, [autoPairs, manualPairs]);

  const rollup = rollupSpend(scans, effectivePairs);
  const hasReceipt = receiptLines.length > 0 || !!imageData;

  const tierCounts = useMemo(() => {
    const counts = { upc: 0, "canonical+brand": 0, canonical: 0, fuzzy: 0, manual: 0 };
    for (const m of effectivePairs.values()) {
      if (m?.tier && counts[m.tier] != null) counts[m.tier] += 1;
    }
    return counts;
  }, [effectivePairs]);
  const matchedCount = effectivePairs.size;
  const unmatchedCount = rollup.unmatched.length;

  const { getInfo } = useIngredientInfo() || {};
  const notReady = useMemo(() => {
    let n = 0;
    for (const s of scans) {
      if (!s.canonicalId) continue;
      if (!readinessFor(s.canonicalId, getInfo).ready) n += 1;
    }
    return n;
  }, [scans, getInfo]);

  async function handleReceiptPhoto(e) {
    const file = e.target.files?.[0];
    if (!file) return;
    setError(null);
    setOcrDiagnostic(null);
    setPhase("receipt");
    try {
      const { base64, mediaType } = await compressImage(file, {
        maxDimension: 1100,
        jpegQuality: 0.8,
      });
      setImageData({ base64, mediaType });
      const tripRegistry = buildTripCanonicalRegistry(scans);
      const { data, error: fnErr } = await supabase.functions.invoke("scan-receipt", {
        body: { image: base64, mediaType, ingredients: tripRegistry },
      });
      if (fnErr) throw fnErr;
      if (data?.error) throw new Error(data.error);

      const lines = Array.isArray(data?.items) ? data.items : [];
      const meta = {
        store:      data?.store ?? null,
        date:       data?.date ?? null,
        totalCents: typeof data?.totalCents === "number" ? data.totalCents : null,
      };
      setOcrDiagnostic({
        itemsLength: lines.length,
        store:       meta.store,
        date:        meta.date,
        totalCents:  meta.totalCents,
        message:     null,
      });
      setReceiptMeta(meta);
      setReceiptLines(lines);
      setAutoPairs(matchReceiptLines(scans, lines));
      setManualPairs(new Map());
      setPhase("summary");

      if (lines.length === 0) {
        setError(
          `OCR returned 0 items. Store: ${meta.store ?? "—"}, total: ${
            typeof meta.totalCents === "number" ? formatCents(meta.totalCents) : "—"
          }. Try a clearer photo or zoom in on the line items.`
        );
      }
    } catch (e) {
      console.warn("[checkout] receipt scan failed:", e);
      setOcrDiagnostic({
        itemsLength: 0, store: null, date: null, totalCents: null,
        message: e?.message || String(e),
      });
      setError(`scan-receipt edge function failed: ${e?.message || String(e)}`);
      setPhase("summary");
    }
  }

  async function handleCommit() {
    if (!trip?.id || !userId) return;
    setPhase("committing");
    setError(null);
    try {
      const out = await commitTrip({
        scans,
        priceByScanId: effectivePairs,
        placementOverrides,
        receiptMeta: receiptMeta?.store || receiptMeta?.date || receiptMeta?.totalCents
          ? receiptMeta : null,
        imageData, userId, isAdmin, supabase,
        tripId: trip.id,
        tripStoreName: trip?.storeName || null,
      });
      setResult(out);
      setPhase("done");
      const hadErrors = (out?.errors?.length || 0) > 0
        || (out?.pantryRowIds?.length || 0) === 0;
      if (!hadErrors) {
        // Trip committed cleanly; clear the local checkout cache so
        // the next trip starts blank.
        invalidateCache(CHECKOUT_CACHE_KEY(trip.id));
        setTimeout(() => {
          tripActions.cancel();
          onComplete?.(out);
        }, 1200);
      }
    } catch (e) {
      console.warn("[checkout] commit failed:", e);
      setError(e?.message || "Commit failed. Try again.");
      setPhase("summary");
    }
  }

  function handleManualPair(scanId, lineIndex) {
    const line = receiptLines[lineIndex];
    setManualPairs((prev) => {
      const next = new Map(prev);
      next.set(scanId, {
        lineIndex,
        priceCents: typeof line.priceCents === "number" ? line.priceCents : null,
        tier: "manual",
        lineText: line.rawText || null,
      });
      return next;
    });
    setExpandedScanId(null);
  }

  function handleUnpair(scanId) {
    setManualPairs((prev) => {
      const next = new Map(prev);
      next.set(scanId, null);
      return next;
    });
    setExpandedScanId(null);
  }

  // ── Done state ──
  if (phase === "done" && result) {
    const pantryFailed = result.errors?.some?.((e) => e.op === "pantry");
    const noRowsLanded = (result.pantryRowIds?.length || 0) === 0 && scans.length > 0;
    const failed = pantryFailed || noRowsLanded;
    const headline = failed ? "Commit had problems." : "Trip committed.";
    const detail = failed
      ? `${result.pantryRowIds?.length || 0} of ${scans.length} item${scans.length === 1 ? "" : "s"} landed. Check the console for [commit-trip] errors.`
      : `${result.pantryRowIds.length} item${result.pantryRowIds.length === 1 ? "" : "s"} added` +
        (rollup.totalCents > 0 ? ` · ${formatCents(rollup.totalCents)}` : "");
    return (
      <div style={{
        flex: 1, display: "flex", flexDirection: "column",
        alignItems: "center", justifyContent: "center",
        padding: SPACE.section, gap: SPACE.lg,
      }}>
        <div style={{
          fontFamily: FONT.itemTitle, fontStyle: "italic", fontSize: 28,
          color: failed ? COLOR.roseDim : COLOR.ink, textAlign: "center",
        }}>
          {headline}
        </div>
        <div style={{
          fontFamily: FONT.forma, fontSize: 14,
          color: COLOR.muted, textAlign: "center",
        }}>
          {detail}
        </div>
        {failed && Array.isArray(result.errors) && result.errors.length > 0 && (
          <div style={{
            fontFamily: FONT.mono, fontSize: 12, color: COLOR.roseDim,
            background: COLOR.roseDeep, border: `1px solid ${COLOR.roseDim}`,
            borderRadius: RADIUS.md, padding: SPACE.md, maxWidth: 360,
            textAlign: "left", whiteSpace: "pre-wrap", lineHeight: 1.4,
          }}>
            {result.errors.map((e) => `${e.op}: ${e.error}`).join("\n")}
          </div>
        )}
      </div>
    );
  }

  // ── Hero ──
  const renderHero = () => {
    const total = rollup.totalCents;
    const totalLabel = hasReceipt
      ? (total > 0 ? formatCents(total) : "$0.00")
      : `${scans.length}`;
    const subtitle = hasReceipt
      ? `${matchedCount} priced · ${unmatchedCount} without`
      : `scan${scans.length === 1 ? "" : "s"} ready · no receipt yet`;

    return (
      <div style={{
        position: "relative",
        padding: SPACE.lg,
        background: COLOR.ground,
        border: `1px solid ${COLOR.edge}`,
        borderRadius: RADIUS.md,
      }}>
        <div style={{ display: "flex", alignItems: "flex-start", gap: SPACE.lg }}>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{
              fontFamily: FONT.itemTitle, fontStyle: "italic",
              fontSize: 40, lineHeight: 1.05, color: COLOR.ink,
              fontVariantNumeric: "tabular-nums", letterSpacing: "-0.01em",
            }}>
              {totalLabel}
            </div>
            <div style={{
              fontFamily: FONT.forma, fontSize: 13,
              color: COLOR.muted, marginTop: 6, lineHeight: 1.4,
            }}>
              {subtitle}
              {receiptMeta.store ? ` · ${receiptMeta.store}` : ""}
            </div>
          </div>

          {imageData?.base64 && (
            <div style={{
              flex: "0 0 auto", width: 56, height: 72,
              borderRadius: RADIUS.sm, border: `1px solid ${COLOR.edge}`,
              overflow: "hidden", background: COLOR.ground,
            }}>
              <img
                src={`data:${imageData.mediaType};base64,${imageData.base64}`}
                alt="receipt"
                style={{ width: "100%", height: "100%", objectFit: "cover", display: "block" }}
              />
            </div>
          )}
        </div>

        {hasReceipt && scans.length > 0 && (
          <>
            <div style={{
              display: "flex", height: 6, borderRadius: 3,
              overflow: "hidden", background: COLOR.edge, marginTop: SPACE.lg,
            }}>
              {TIERS.map((t) =>
                tierCounts[t.key] > 0 ? (
                  <div key={t.key} style={{ flex: tierCounts[t.key], background: t.color }} />
                ) : null
              )}
              {unmatchedCount > 0 && (
                <div style={{ flex: unmatchedCount, background: "transparent" }} />
              )}
            </div>
            <div style={{
              display: "flex", flexWrap: "wrap",
              gap: `${SPACE.xs}px ${SPACE.md}px`,
              marginTop: SPACE.sm,
              fontFamily: FONT.mono, fontSize: 12,
              letterSpacing: "0.10em", color: COLOR.muted,
            }}>
              {TIERS.filter((t) => tierCounts[t.key] > 0).map((t) => (
                <span key={t.key} style={{ display: "inline-flex", alignItems: "center", gap: 5 }}>
                  <span style={{
                    width: 7, height: 7, borderRadius: 2,
                    background: t.color, display: "inline-block",
                  }} />
                  {t.label} {tierCounts[t.key]}
                </span>
              ))}
              {unmatchedCount > 0 && (
                <span style={{ display: "inline-flex", alignItems: "center", gap: 5 }}>
                  <span style={{
                    width: 7, height: 7, borderRadius: 2,
                    border: `1px solid ${COLOR.edge}`, display: "inline-block",
                  }} />
                  UNPAIRED {unmatchedCount}
                </span>
              )}
            </div>
          </>
        )}
      </div>
    );
  };

  // ── OCR diagnostic strip ──
  const renderOcrDiagnostic = () => {
    if (!ocrDiagnostic) return null;
    return (
      <div style={{
        marginTop: SPACE.md,
        padding: `${SPACE.sm}px ${SPACE.md}px`,
        background: COLOR.ground,
        border: `1px solid ${COLOR.edge}`,
        borderRadius: RADIUS.sm,
        fontFamily: FONT.mono, fontSize: 12,
        color: COLOR.muted, lineHeight: 1.5,
      }}>
        <strong style={{ color: COLOR.ink, fontWeight: 600 }}>Receipt scan</strong>
        {" · "}{ocrDiagnostic.itemsLength} line item{ocrDiagnostic.itemsLength === 1 ? "" : "s"}
        {" · store "}{ocrDiagnostic.store ?? "—"}
        {" · date "}{ocrDiagnostic.date ?? "—"}
        {" · total "}
        {typeof ocrDiagnostic.totalCents === "number" ? formatCents(ocrDiagnostic.totalCents) : "—"}
        {ocrDiagnostic.message && (
          <div style={{ color: COLOR.rose, marginTop: 4 }}>
            error: {ocrDiagnostic.message}
          </div>
        )}
      </div>
    );
  };

  // ── Pair picker ── shared by paired (re-pair) and unpaired (pair).
  // Shows scan identity at top; "mark as no match"; every receipt
  // line stacked with a 3-axis grid (UPC, canon, brand) where
  // matching axes light up leaf.
  const renderPairPicker = (scan) => {
    const currentPair = effectivePairs.get(scan.id);
    const currentLineIndex = currentPair?.lineIndex;

    const candidates = receiptLines.map((line, lineIndex) => {
      const claimedBy = [...effectivePairs.entries()]
        .find(([, p]) => p?.lineIndex === lineIndex)?.[0] || null;
      return {
        line, lineIndex,
        claimedBySomeoneElse: claimedBy && claimedBy !== scan.id,
        isCurrent: lineIndex === currentLineIndex,
      };
    });

    return (
      <div style={{
        marginTop: SPACE.sm,
        background: COLOR.ground,
        border: `1px solid ${COLOR.edge}`,
        borderRadius: RADIUS.sm,
      }}>
        <div style={{
          padding: `${SPACE.sm}px ${SPACE.md}px`,
          borderBottom: `1px solid ${COLOR.edge}`,
          background: COLOR.leafDeep,
        }}>
          <div style={{
            fontFamily: FONT.mono, fontSize: 12,
            color: COLOR.muted, marginBottom: 6,
            letterSpacing: "0.18em", textTransform: "uppercase",
          }}>
            Your scan
          </div>
          <div style={{
            display: "grid",
            gridTemplateColumns: "60px 1fr",
            gap: `4px ${SPACE.sm}px`,
            fontFamily: FONT.mono, fontSize: 13, color: COLOR.ink,
          }}>
            <span style={{ color: COLOR.muted }}>name</span>
            <span style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
              {scan.displayName || scan.productName || "—"}
            </span>
            <span style={{ color: COLOR.muted }}>upc</span>
            <span style={{ fontVariantNumeric: "tabular-nums" }}>
              {scan.barcodeUpc || "—"}
            </span>
            <span style={{ color: COLOR.muted }}>canon</span>
            <span>{scan.canonicalId || "—"}</span>
            <span style={{ color: COLOR.muted }}>brand</span>
            <span>{scan.brand || "—"}</span>
          </div>
        </div>

        <button
          onClick={() => handleUnpair(scan.id)}
          className="mcm-focusable"
          style={{
            display: "block", width: "100%", textAlign: "left",
            padding: `${SPACE.sm}px ${SPACE.md}px`,
            background: "transparent", border: "none",
            borderBottom: `1px solid ${COLOR.edge}`,
            cursor: "pointer",
            fontFamily: FONT.mono, fontSize: 13, color: COLOR.muted,
            fontStyle: "italic",
          }}
        >
          mark this scan as not on the receipt
        </button>

        {candidates.length === 0 ? (
          <div style={{
            padding: SPACE.md,
            fontFamily: FONT.forma, fontStyle: "italic",
            fontSize: 13, color: COLOR.muted, textAlign: "center",
          }}>
            no receipt lines available
          </div>
        ) : candidates.map(({ line, lineIndex, claimedBySomeoneElse, isCurrent }) => (
          <button
            key={lineIndex}
            onClick={() =>
              claimedBySomeoneElse ? null : handleManualPair(scan.id, lineIndex)
            }
            disabled={claimedBySomeoneElse}
            className={claimedBySomeoneElse ? undefined : "mcm-focusable"}
            style={{
              display: "block", width: "100%", textAlign: "left",
              padding: `${SPACE.sm}px ${SPACE.md}px`,
              background: isCurrent ? COLOR.leafDeep : "transparent",
              border: "none",
              borderBottom: `1px solid ${COLOR.edge}`,
              cursor: claimedBySomeoneElse ? "not-allowed" : "pointer",
              opacity: claimedBySomeoneElse ? 0.4 : 1,
            }}
          >
            <div style={{
              display: "flex", alignItems: "baseline",
              justifyContent: "space-between", gap: SPACE.sm,
            }}>
              <span style={{
                fontFamily: FONT.mono, fontSize: 13,
                color: COLOR.ink, flex: 1,
                overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap",
              }}>
                {isCurrent ? "current → " : ""}{line.rawText || "(blank line)"}
              </span>
              <span style={{
                fontFamily: FONT.mono, fontSize: 13,
                fontVariantNumeric: "tabular-nums",
                color: COLOR.ink, fontWeight: 600,
              }}>
                {typeof line.priceCents === "number" ? formatCents(line.priceCents) : "—"}
              </span>
            </div>
            <div style={{
              display: "grid",
              gridTemplateColumns: "60px 1fr",
              gap: `2px ${SPACE.sm}px`,
              fontFamily: FONT.mono, fontSize: 12,
              color: COLOR.muted, marginTop: 4,
            }}>
              <span>upc</span>
              <span style={{ fontVariantNumeric: "tabular-nums" }}>{line.barcode || "—"}</span>
              <span>canon</span>
              <span style={{
                color: line.canonicalId === scan.canonicalId && line.canonicalId
                  ? COLOR.leaf : COLOR.muted,
              }}>
                {line.canonicalId || "—"}
              </span>
              <span>brand</span>
              <span style={{
                color: line.brand && scan.brand &&
                  line.brand.toLowerCase() === scan.brand.toLowerCase()
                  ? COLOR.leaf : COLOR.muted,
              }}>
                {line.brand || "—"}
              </span>
              {claimedBySomeoneElse && (
                <>
                  <span>status</span>
                  <span style={{ color: COLOR.rose, fontStyle: "italic" }}>
                    paired to another scan
                  </span>
                </>
              )}
            </div>
          </button>
        ))}
      </div>
    );
  };

  // ── Single unified scan row ──
  // Collapsed: status at a glance + scan/receipt identity strips for
  // audit. Tap title area → expand to picker. Bottom-right has a
  // separate delete affordance so the user can remove duplicate or
  // erroneous scans without leaving the screen ("always allow an
  // escape at any point").
  const renderScanRow = (scan) => {
    const pair = effectivePairs.get(scan.id) || null;
    const expanded = expandedScanId === scan.id;
    const tier = pair ? TIER_BY_KEY[pair.tier] : null;
    const isPaired = !!pair;
    const reason = !isPaired && hasReceipt ? explainUnmatched(scan, receiptLines) : null;
    const recptLine = isPaired ? receiptLines[pair.lineIndex] : null;

    const isArmed = armedDeleteScanId === scan.id;
    const handleDelete = (e) => {
      e.stopPropagation();
      if (!isArmed) {
        setArmedDeleteScanId(scan.id);
        return;
      }
      tripActions.deleteScan(scan.id);
      setArmedDeleteScanId(null);
      if (expandedScanId === scan.id) setExpandedScanId(null);
    };

    return (
      <div key={scan.id} style={{
        position: "relative",
        background: !isPaired && hasReceipt ? COLOR.roseDeep : "transparent",
        borderBottom: `1px solid ${COLOR.edge}`,
      }}>
        {/* ✕ delete — top-right corner. First tap arms (✕ → ‼ rose);
            second tap within DELETE_ARM_MS commits. 4s auto-disarm.
            No native confirm dialog. */}
        <button
          type="button"
          onClick={handleDelete}
          aria-label={isArmed
            ? `Tap again to remove ${scanTitle(scan) || "scan"}`
            : `Remove ${scanTitle(scan) || "scan"}`}
          className="mcm-focusable"
          style={{
            position: "absolute",
            top: 6, right: 6,
            background: isArmed ? COLOR.roseDeep : "transparent",
            border: isArmed ? `1px solid ${COLOR.roseDim}` : "none",
            padding: isArmed ? "6px 10px" : 8,
            cursor: "pointer",
            fontFamily: FONT.mono,
            fontSize: isArmed ? 14 : 16,
            fontWeight: isArmed ? 600 : 400,
            letterSpacing: isArmed ? "0.10em" : 0,
            color: isArmed ? COLOR.rose : COLOR.muted,
            lineHeight: 1,
            borderRadius: isArmed ? RADIUS.sm : 0,
            zIndex: 1,
          }}
        >
          {isArmed ? "tap to confirm" : "✕"}
        </button>
        <button
          onClick={() => hasReceipt
            ? setExpandedScanId(expanded ? null : scan.id)
            : null}
          disabled={!hasReceipt}
          className={hasReceipt ? "mcm-focusable" : undefined}
          style={{
            display: "block", width: "100%", textAlign: "left",
            padding: `${SPACE.md}px 36px ${SPACE.md}px ${SPACE.md}px`,
            background: "transparent", border: "none",
            cursor: hasReceipt ? "pointer" : "default",
          }}
        >
          <div style={{
            display: "flex", alignItems: "baseline",
            justifyContent: "space-between", gap: SPACE.sm,
          }}>
            <span style={{
              fontFamily: FONT.itemTitle, fontStyle: "italic",
              fontSize: 17, color: COLOR.ink,
              flex: 1, overflow: "hidden",
              textOverflow: "ellipsis", whiteSpace: "nowrap",
            }}>
              {scanTitle(scan) || "(unnamed)"}
              {scan.qty > 1 ? ` ×${scan.qty}` : ""}
            </span>
            {isPaired ? (
              <span style={{
                fontFamily: FONT.forma, fontWeight: 600, fontSize: 14,
                fontVariantNumeric: "tabular-nums",
                color: pair.priceCents == null ? COLOR.muted : COLOR.ink,
              }}>
                {pair.priceCents == null ? "—" : formatCents(pair.priceCents)}
              </span>
            ) : hasReceipt ? (
              <span style={{
                fontFamily: FONT.forma, fontStyle: "italic", fontSize: 13,
                color: COLOR.muted,
              }}>
                no price
              </span>
            ) : null}
          </div>

          {/* Tier line — explicit "MATCH" prefix + uppercase tier
              label + receipt's printed line in QUOTES so the rawText
              can't be mistaken for a UPC. Tier label is uppercase to
              match the hero progress-bar legend. */}
          {isPaired && (
            <div style={{
              fontFamily: FONT.mono, fontSize: 12,
              marginTop: 6, lineHeight: 1.5,
              overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap",
            }}>
              <span style={{ color: COLOR.muted, fontWeight: 600, letterSpacing: "0.14em", textTransform: "uppercase" }}>
                match
              </span>
              {" "}
              <span style={{
                color: tier?.color || COLOR.muted,
                fontWeight: 600, letterSpacing: "0.06em",
              }}>
                {tier?.label || (pair.tier || "?").toUpperCase()}
              </span>
              <span style={{ color: COLOR.muted }}>
                {" · receipt line "}
              </span>
              <span style={{ color: COLOR.ink }}>
                {pair.lineText ? `"${pair.lineText}"` : "—"}
              </span>
            </div>
          )}

          {reason && (
            <div style={{
              fontFamily: FONT.mono, fontSize: 12,
              color: COLOR.rose, marginTop: 6, lineHeight: 1.5,
              whiteSpace: "normal", wordBreak: "break-word",
            }}>
              <span style={{
                fontWeight: 600, letterSpacing: "0.14em",
                textTransform: "uppercase", marginRight: SPACE.xs,
              }}>
                why
              </span>
              {reason}
            </div>
          )}

          {/* SCAN identity (always shown). RECPT identity (only when
              paired). Two strips align column-wise for digit-by-digit
              UPC audit — equal digits in equal column positions. */}
          <IdentityStrip
            side="scan"
            upc={scan.barcodeUpc}
            canonicalId={scan.canonicalId}
            brand={scan.brand}
            sideColor={COLOR.muted}
          />
          {recptLine && (
            <IdentityStrip
              side="recpt"
              upc={recptLine.barcode}
              canonicalId={recptLine.canonicalId}
              brand={recptLine.brand}
              sideColor={tier?.color || COLOR.muted}
            />
          )}

          {hasReceipt && (
            <div style={{
              fontFamily: FONT.mono, fontSize: 12,
              color: COLOR.muted, marginTop: 8,
              letterSpacing: "0.14em", textTransform: "uppercase",
            }}>
              {expanded
                ? "▴ collapse"
                : isPaired
                  ? "▾ details"
                  : "▾ pair this scan"}
            </div>
          )}
        </button>

        {expanded && hasReceipt && (
          <div style={{ padding: `0 ${SPACE.md}px ${SPACE.md}px` }}>
            {renderPairPicker(scan)}
          </div>
        )}
      </div>
    );
  };

  // ── Main render ──
  return (
    <div style={{ display: "flex", flexDirection: "column", flex: 1, minHeight: 0 }}>
      <div style={{
        display: "flex", alignItems: "center", justifyContent: "space-between",
        padding: "14px 18px", borderBottom: `1px solid ${COLOR.edge}`,
        flex: "0 0 auto",
      }}>
        <div style={sectionLabel}>Checkout</div>
        {onBack && (
          <button
            onClick={onBack}
            disabled={phase === "committing"}
            className="mcm-focusable"
            style={{
              fontFamily: FONT.mono, fontSize: 12,
              letterSpacing: "0.14em", textTransform: "uppercase",
              color: COLOR.muted, background: "transparent",
              border: "none", padding: "4px 8px",
              cursor: phase === "committing" ? "not-allowed" : "pointer",
            }}
          >
            ← back
          </button>
        )}
      </div>

      <div style={{
        flex: 1, minHeight: 0, overflowY: "auto",
        padding: "18px",
      }}>
        {error && (
          <div style={{
            padding: SPACE.md, background: COLOR.roseDeep,
            border: `1px solid ${COLOR.roseDim}`, borderRadius: RADIUS.md,
            fontFamily: FONT.mono, fontSize: 12, lineHeight: 1.5,
            color: COLOR.rose, marginBottom: SPACE.md,
            whiteSpace: "pre-wrap",
          }}>
            {error}
          </div>
        )}

        {renderHero()}
        {renderOcrDiagnostic()}

        {/* Receipt CTA — primary pressable when no receipt; quiet
            re-scan link when one is loaded. */}
        {!hasReceipt ? (
          <label
            className="mcm-focusable"
            style={{
              display: "flex", flexDirection: "column",
              gap: 4, alignItems: "center",
              padding: SPACE.lg, marginTop: SPACE.lg,
              background: COLOR.leafDeep,
              border: `1px solid ${COLOR.leaf}`,
              borderRadius: RADIUS.md,
              cursor: phase === "receipt" ? "wait" : "pointer",
              textAlign: "center",
            }}
          >
            <span style={{
              fontFamily: FONT.itemTitle, fontStyle: "italic",
              fontSize: 19, color: COLOR.leaf, fontWeight: 500,
            }}>
              {phase === "receipt" ? "Reading receipt…" : "Snap your receipt"}
            </span>
            <span style={{
              fontFamily: FONT.forma, fontSize: 13,
              color: COLOR.muted,
            }}>
              We'll match each line to a scan. Optional.
            </span>
            <input
              type="file" accept="image/*"
              onChange={handleReceiptPhoto}
              style={{ display: "none" }}
              disabled={phase === "receipt"}
            />
          </label>
        ) : (
          <div style={{ display: "flex", justifyContent: "flex-end", marginTop: SPACE.sm }}>
            <label
              className="mcm-focusable"
              style={{
                fontFamily: FONT.mono, fontSize: 12,
                letterSpacing: "0.14em", textTransform: "uppercase",
                color: COLOR.muted,
                cursor: phase === "receipt" ? "wait" : "pointer",
                padding: "4px 0",
              }}
            >
              {phase === "receipt" ? "Reading receipt…" : "Re-scan receipt"}
              <input
                type="file" accept="image/*"
                onChange={handleReceiptPhoto}
                style={{ display: "none" }}
                disabled={phase === "receipt"}
              />
            </label>
          </div>
        )}

        {phase === "receipt" ? (
          <div style={{
            marginTop: SPACE.section,
            padding: `${SPACE.section}px ${SPACE.lg}px`,
            background: COLOR.ground,
            border: `1px solid ${COLOR.edge}`,
            borderRadius: RADIUS.md,
            display: "flex", flexDirection: "column",
            alignItems: "center", gap: SPACE.md,
            textAlign: "center",
          }}>
            <div style={{ display: "flex", gap: 6 }}>
              {[0, 1, 2].map((i) => (
                <span key={i} style={{
                  width: 8, height: 8, borderRadius: 4,
                  background: COLOR.leaf,
                  animation: `mcm-pulse 1.2s ease-in-out ${i * 0.15}s infinite`,
                }} />
              ))}
            </div>
            <div style={{
              fontFamily: FONT.itemTitle, fontStyle: "italic",
              fontSize: 22, color: COLOR.ink,
            }}>
              Reading receipt…
            </div>
            <div style={{
              fontFamily: FONT.forma, fontSize: 13,
              color: COLOR.muted, maxWidth: 280, lineHeight: 1.5,
            }}>
              Reading line items, prices, and barcodes. Usually 8 to
              15 seconds for a typical grocery receipt.
            </div>
            <style>{`
              @keyframes mcm-pulse {
                0%, 100% { opacity: 0.3; transform: translateY(0); }
                50%      { opacity: 1;   transform: translateY(-3px); }
              }
            `}</style>
          </div>
        ) : (
          <>
            {/* Single unified scan list — every scan visible, tap to
                expand into the picker. The "matched" / "unmatched
                scans" / "unclaimed receipt lines" sections are gone;
                state is per-row and unclaimed lines surface inside
                the picker as candidates. */}
            {scans.length > 0 && (
              <>
                <div style={{
                  ...sectionLabel,
                  padding: `${SPACE.lg}px 0 ${SPACE.sm}px 0`,
                  display: "flex", justifyContent: "space-between", alignItems: "baseline",
                }}>
                  <span>Scans</span>
                  <span>
                    {hasReceipt
                      ? `${matchedCount} of ${scans.length} paired`
                      : `${scans.length} ready`}
                  </span>
                </div>
                <div style={{ borderTop: `1px solid ${COLOR.edge}` }}>
                  {scans.map(renderScanRow)}
                </div>
              </>
            )}
          </>
        )}
      </div>

      {notReady > 0 && (
        <div style={{
          padding: `8px 18px`, background: COLOR.roseDeep,
          borderTop: `1px solid ${COLOR.roseDim}`,
          fontFamily: FONT.forma, fontSize: 12,
          color: COLOR.rose, flex: "0 0 auto",
        }}>
          {notReady} item{notReady === 1 ? "" : "s"} will commit without
          full nutrition or density data. Calorie rollups and unit
          conversions show dashes until enrichment lands.
        </div>
      )}

      <div style={{
        padding: `16px 18px calc(env(safe-area-inset-bottom, 0px) + 16px) 18px`,
        borderTop: `1px solid ${COLOR.edge}`,
        flex: "0 0 auto", background: COLOR.ground,
      }}>
        {(() => {
          const isLocked =
            phase === "committing" ||
            phase === "receipt" ||
            scans.length === 0;
          return (
            <button
              onClick={handleCommit}
              disabled={isLocked}
              className={isLocked ? undefined : "mcm-focusable"}
              style={{
                width: "100%", padding: "16px 18px",
                fontFamily: FONT.mono, fontSize: 14,
                letterSpacing: "0.18em", textTransform: "uppercase",
                fontWeight: 600,
                color: hasReceipt ? COLOR.night : COLOR.leaf,
                background: hasReceipt ? COLOR.leaf : "transparent",
                border: `1px solid ${COLOR.leaf}`,
                borderRadius: RADIUS.md,
                cursor: isLocked ? "not-allowed" : "pointer",
                opacity: isLocked ? 0.45 : 1,
              }}
            >
              {phase === "committing"
                ? "Committing…"
                : phase === "receipt"
                ? "Reading receipt…"
                : `Commit ${scans.length} item${scans.length === 1 ? "" : "s"}`}
            </button>
          );
        })()}
      </div>
    </div>
  );
}
